import HeaderSection from '../components/Shared/HeaderSection'
import TicketCategories from '../modules/TicketCategories'
import TicketQueues from '../modules/TicketQueues'
import SlaSettings from '../modules/SlaSettings'
import TicketStatus from '../modules/TicketStatus'
import TicketPriorities from '../modules/TicketPriorities'
import Users from '../modules/Users'
import NotificationSettings from '../modules/NotificationSettings'
import AuthAccessValidator from '../components/Auth/AuthAccessValidator'
import usePersistedState from '../hooks/usePersistedState'

/**
 * Secciones de Configuración. `group` agrupa la navegación lateral.
 * Agregar una sección = agregar una entrada aquí.
 */
const SECTIONS = [
  {
    key: 'general',
    label: 'Usuarios',
    icon: 'fa-users',
    group: 'Equipo',
    Component: Users,
  },
  {
    key: 'queues',
    label: 'Colas de atención',
    icon: 'fa-people-arrows',
    group: 'Equipo',
    Component: TicketQueues,
  },
  {
    key: 'sla',
    label: 'Tiempos de SLA',
    icon: 'fa-stopwatch',
    group: 'Equipo',
    Component: SlaSettings,
  },
  {
    key: 'categories',
    label: 'Categorías',
    icon: 'fa-layer-group',
    group: 'Catálogos',
    Component: TicketCategories,
  },
  {
    key: 'priority',
    label: 'Prioridades',
    icon: 'fa-flag',
    group: 'Catálogos',
    Component: TicketPriorities,
  },
  {
    key: 'status',
    label: 'Estados',
    icon: 'fa-circle-half-stroke',
    group: 'Catálogos',
    Component: TicketStatus,
  },
  {
    key: 'notifications',
    label: 'Notificaciones',
    icon: 'fa-bell',
    group: 'Sistema',
    Component: NotificationSettings,
  },
]

const GROUPS = [...new Set(SECTIONS.map((s) => s.group))]

/**
 * Configuración: navegación lateral compacta y el panel de la sección activa,
 * que ocupa el alto disponible y scrollea por dentro.
 */
const Settings = () => {
  const [activeKey, setActiveKey] = usePersistedState(
    'settings.section',
    'general'
  )
  const active = SECTIONS.find((s) => s.key === activeKey) || SECTIONS[0]
  const ActiveComponent = active.Component

  return (
    <AuthAccessValidator AccessNames={['ticket_support']} showError={true}>
      <div className="page-fill settings-page">
        <HeaderSection
          compact
          title="Configuración"
          subtitle="Equipo, catálogos y notificaciones del sistema de tickets"
          icon="fa-solid fa-gear"
        />

        <div className="st-layout">
          <nav className="st-nav" aria-label="Secciones de configuración">
            {GROUPS.map((group) => (
              <div key={group} className="st-group">
                <span className="st-group-label">{group}</span>
                {SECTIONS.filter((s) => s.group === group).map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={`st-link ${active.key === section.key ? 'is-active' : ''}`}
                    onClick={() => setActiveKey(section.key)}
                    aria-current={
                      active.key === section.key ? 'page' : undefined
                    }
                  >
                    <i className={`fa-solid ${section.icon}`} />
                    <span>{section.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <main className="st-panel">
            <ActiveComponent key={active.key} />
          </main>
        </div>

        <style>{`
          .st-layout {
            flex: 1 1 auto; min-height: 0; display: flex; gap: 12px;
          }
          .st-nav {
            flex: 0 0 200px; display: flex; flex-direction: column; gap: 14px;
            padding: 10px; border: 1px solid var(--gm-border); border-radius: 12px;
            background: var(--gm-surface); overflow-y: auto;
          }
          .st-group { display: flex; flex-direction: column; gap: 2px; }
          .st-group-label {
            padding: 0 8px 4px; font-size: 0.65rem; font-weight: 700;
            letter-spacing: 0.06em; text-transform: uppercase; color: var(--gm-text-faint);
          }
          .st-link {
            display: flex; align-items: center; gap: 10px; width: 100%;
            padding: 7px 10px; border: none; border-radius: 8px; background: transparent;
            color: var(--gm-text-muted); font-size: 0.84rem; font-weight: 500; text-align: left;
            transition: background 0.15s ease, color 0.15s ease;
          }
          .st-link i { width: 16px; text-align: center; font-size: 0.8rem; }
          .st-link:hover { background: var(--gm-surface-2); color: var(--gm-text); }
          .st-link.is-active { background: var(--gm-primary-soft); color: var(--gm-primary); font-weight: 600; }

          .st-panel {
            flex: 1 1 auto; min-width: 0; min-height: 0; overflow-y: auto;
            padding: 16px; border: 1px solid var(--gm-border); border-radius: 12px;
            background: var(--gm-surface);
          }

          /* En móvil la navegación pasa arriba, en una fila desplazable. */
          @media (max-width: 767px) {
            .st-layout { flex-direction: column; }
            .st-nav { flex: 0 0 auto; flex-direction: row; overflow-x: auto; gap: 4px; padding: 6px; }
            .st-group { flex-direction: row; gap: 4px; }
            .st-group-label { display: none; }
            .st-link { width: auto; white-space: nowrap; }
          }
        `}</style>
      </div>
    </AuthAccessValidator>
  )
}

export default Settings
